/**
 * Algolia Product Detail Page (PDP) Tracking
 *
 * Fires an "Add To Cart" conversion event to Algolia when a shopper adds a
 * product to cart on a PDP that they reached via search/autocomplete.
 *
 * Attribution is resolved through window.AlgoliaInsights.resolveAttribution(),
 * which reads the click/search records algolia-autocomplete.js and
 * algolia-instantsearch.js already write to sessionStorage.
 *
 * WHY THIS DOESN'T PARSE THE /cart/add RESPONSE:
 * An earlier version read variant_id off the raw /cart/add.js response body.
 * That assumes a specific Shopify response shape which, on this storefront,
 * did not hold up in testing — the conversion never fired even though the
 * add itself succeeded. Rather than guess at another shape, this version
 * diffs Shopify's own /cart.js (the canonical, GET-able cart state) before
 * and after a cart-mutating action. /cart.js's schema is Shopify's stable
 * public contract, so this works regardless of what any specific mutation
 * endpoint returns — including if the theme adds via a form post, a custom
 * cart micro-service, or anything else that isn't a plain fetch('/cart/add').
 *
 * Two independent triggers feed the same check, and naturally de-duplicate
 * against each other (whichever fires second sees no further quantity
 * increase and no-ops):
 *   1. fetch/XHR interception on /cart/add (fast path).
 *   2. a 'cart:updated' or 'cart:changed' DOM event (same events
 *      theme.liquid's own storeCartId() already listens for) — this is the
 *      more reliable signal if the theme's add path doesn't go through a
 *      fetch/XHR call this script can see (e.g. a captured fetch reference
 *      taken before this script ran, a custom element, etc).
 *
 * NOTE: product-view tracking (e.g. "viewed this PDP") is intentionally not
 * implemented here — see insights-utils.js's SURFACE map / baseline-parity
 * comment. That's a separate analytics decision, not a bug fix.
 */
(function () {
  'use strict';

  var DEBUG = true; // flip to false once this is confirmed working in prod
  function log() {
    if (DEBUG && window.console) console.debug.apply(console, ['[Algolia PDP]'].concat([].slice.call(arguments)));
  }

  function waitForInsights(callback) {
    if (window.AlgoliaInsights) {
      callback();
      return;
    }
    var attempts = 0;
    var interval = setInterval(function () {
      if (window.AlgoliaInsights || attempts > 50) {
        clearInterval(interval);
        if (window.AlgoliaInsights) {
          callback();
        } else {
          console.warn('[Algolia PDP] AlgoliaInsights not available after 5s — conversion tracking disabled.');
        }
      }
      attempts++;
    }, 100);
  }

  // variantId (string) -> quantity currently in cart, per the last /cart.js read.
  var cartSnapshot = null;
  var checkInFlight = false;

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      return r.json();
    });
  }

  function snapshotFromCart(cart) {
    var map = {};
    (cart.items || []).forEach(function (item) {
      var id = String(item.variant_id != null ? item.variant_id : item.id);
      map[id] = (map[id] || 0) + (item.quantity || 0);
    });
    return map;
  }

  /** Establishes the baseline without firing anything — pre-existing cart
   * contents are not "just added". */
  function primeSnapshot() {
    fetchCart()
      .then(function (cart) {
        cartSnapshot = snapshotFromCart(cart);
        log('baseline cart snapshot', cartSnapshot);
      })
      .catch(function (err) {
        log('failed to prime cart snapshot', err);
      });
  }

  function checkForNewAdds(trigger) {
    if (checkInFlight) return;
    checkInFlight = true;

    fetchCart()
      .then(function (cart) {
        var next = snapshotFromCart(cart);
        var prev = cartSnapshot || {};
        log('checking cart diff (trigger: ' + trigger + ')', { prev: prev, next: next });

        Object.keys(next).forEach(function (variantId) {
          if (next[variantId] > (prev[variantId] || 0)) {
            var attribution = window.AlgoliaInsights.resolveAttribution(variantId);
            log('variant ' + variantId + ' quantity increased — resolveAttribution:', attribution);
            if (!attribution) return; // not attributable to a search/autocomplete session
            window.AlgoliaInsights.publishConversion(
              attribution.surface,
              attribution.indexName,
              attribution.objectID,
              attribution.queryID,
            );
            log('published conversion for variant ' + variantId, attribution);
          }
        });

        cartSnapshot = next;
      })
      .catch(function (err) {
        log('cart diff check failed', err);
      })
      .then(function () {
        checkInFlight = false;
      });
  }

  function init() {
    if (!window.__ALGOLIA_CONFIG) {
      console.warn('[Algolia PDP] __ALGOLIA_CONFIG not available');
      return;
    }

    primeSnapshot();

    // Trigger 1: fetch/XHR to /cart/add. Small delay mirrors theme.liquid's
    // own storeCartId() pattern (setTimeout(..., 200)) — gives the server a
    // moment of consistency before we re-read /cart.js.
    var originalFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var result = originalFetch.apply(this, args);
      var url = args[0] && args[0].url ? args[0].url : args[0];
      if (typeof url === 'string' && url.indexOf('/cart/add') !== -1) {
        log('intercepted fetch to', url);
        result
          .then(function () {
            setTimeout(function () {
              checkForNewAdds('fetch');
            }, 200);
          })
          .catch(function () {
            /* request failed — no confirmed add */
          });
      }
      return result;
    };

    var originalXHROpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (method, url) {
      this._algoliaCartAddUrl = typeof url === 'string' && url.indexOf('/cart/add') !== -1;
      return originalXHROpen.apply(this, arguments);
    };

    var originalXHRSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
      if (this._algoliaCartAddUrl) {
        log('intercepted XHR to /cart/add');
        this.addEventListener('load', function () {
          if (this.status < 200 || this.status >= 300) return;
          setTimeout(function () {
            checkForNewAdds('xhr');
          }, 200);
        });
      }
      return originalXHRSend.apply(this, arguments);
    };

    // Trigger 2: the theme's own cart-mutation signal. This fires
    // regardless of how the add actually happened under the hood, so it
    // catches anything trigger 1 might miss.
    document.addEventListener('cart:updated', function () {
      log('cart:updated event received');
      checkForNewAdds('cart:updated event');
    });
    document.addEventListener('cart:changed', function () {
      log('cart:changed event received');
      checkForNewAdds('cart:changed event');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      waitForInsights(init);
    });
  } else {
    waitForInsights(init);
  }
})();